import React, { useState, useCallback } from "react";
import { BackLink, Title, Text, Pill, Button, Autocomplete } from "components/commons";
import Skeleton from "@material-ui/lab/Skeleton";
import TextField from "@material-ui/core/TextField";
import EditIcon from "@material-ui/icons/Edit";
import { ConfirmModal, SuccessModal } from "components/modals";
import { useModal, useRouter, useApi, useMount, usePermission } from "hooks";
import locale from "localization";
import { Path, Permission, PriceBuildUpStatus } from "enums";
import classNames from "classnames";
import { getPriceBuildUp, getDepots, cancelPriceBuildUp, getPriceBuildUps } from "apis";
import moment from "moment";
import { prettifyProductCodeStyle } from "utils/pretty.utils";
import styles from "./price-build-up-view.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCTS = [
  { name: "Extreme 105", code: "gas105" },
  { name: "Extreme 97", code: "gas97" },
  { name: "Extreme 95", code: "gas95" },
  { name: "Extreme U", code: "gas91" },
  { name: "Diesel", code: "diesel" },
  { name: "Kerosene", code: "kerosene" },
  { name: "LPG", code: "lpg" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt2 = (val) => parseFloat(val || 0).toFixed(2);
const fmt4 = (val) => parseFloat(val || 0).toFixed(4);
const formatDate = (dt) => (dt ? moment(dt).format("MMMM DD, YYYY hh:mm A") : locale.NA);

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
  const upper = (status || "").toUpperCase();
  if (upper === "SCHEDULED") return <Pill cheddar>{locale.scheduled}</Pill>;
  if (upper === "ACTIVE") return <Pill grass>{locale.active}</Pill>;
  if (upper === "EXPIRED") return <Pill cement>{locale.expired}</Pill>;
  if (upper === "CANCELLED") return <Pill deepRed>{locale.cancelled}</Pill>;
  return <Text>{status}</Text>;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PriceBuildUpViewModule = () => {
  const { history, location } = useRouter();
  const { can } = usePermission();
  const priceBuildUpId = location?.state?.priceBuildUpId;

  const canEditPbu = can(Permission.PbuEdit);
  const canCancelPbu = can(Permission.PbuCancel);

  const confirmModal = useModal();
  const successModal = useModal();

  const [priceBuildUp, setPriceBuildUp] = useState(null);
  const [depotMap, setDepotMap] = useState({});
  const [isCancelling, setIsCancelling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cancelReason, setCancelReason] = useState("");
  const [carouselItems, setCarouselItems] = useState([]);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const reasonModal = useModal();

  // ── APIs ──
  const { request: fetchPriceBuildUp } = useApi({
    api: getPriceBuildUp,
    handleOwnError: true,
  });

  const { request: fetchDepots } = useApi({
    api: getDepots,
    handleOwnError: true,
    params: {
      perPage: 1000,
      page: 1,
    },
  });

  const { request: requestCancel } = useApi({
    api: cancelPriceBuildUp,
    handleOwnError: true,
  });

  const { request: fetchPriceBuildUpsRequest } = useApi({
    api: getPriceBuildUps,
    handleOwnError: true,
  });

  // ── Data loading ──
  const loadData = useCallback(
    async (id) => {
      const targetId = id ?? priceBuildUpId;
      if (!targetId) return null;
      const res = await fetchPriceBuildUp({ priceBuildUpId: targetId });
      if (!res) return null;
      setPriceBuildUp({
        ...res,
        previousPumpPriceDisabled: !!res.prevPriceBuildup,
      });
      return res;
    },
    [priceBuildUpId, fetchPriceBuildUp],
  );

  const loadCarousel = useCallback(
    async (pbu) => {
      const stationCode = pbu?.station?.stationCode;
      const effectiveAt = pbu?.effectiveAt;
      const pbuId = pbu?.priceBuildupId ?? pbu?.priceBuildUpId;
      if (!stationCode || !effectiveAt) return;

      setCarouselLoading(true);
      try {
        const baseParams = {
          searchKey: String(stationCode),
          searchType: "stationCode",
          statuses: [
            PriceBuildUpStatus.Active,
            PriceBuildUpStatus.Expired,
            PriceBuildUpStatus.Scheduled,
          ].join(","),
          orderBy: "effectiveAt",
          page: 1,
          perPage: 6,
        };
        const T = moment(effectiveAt);
        const [beforeRes, afterRes] = await Promise.all([
          fetchPriceBuildUpsRequest({
            ...baseParams,
            order: "desc",
            endDate: T.clone().subtract(1, "second").toISOString(),
          }),
          fetchPriceBuildUpsRequest({
            ...baseParams,
            order: "asc",
            startDate: T.clone().add(1, "second").toISOString(),
          }),
        ]);
        const before = [...(beforeRes?.priceBuildUps || [])]
          .filter((item) => (item.priceBuildupId ?? item.priceBuildUpId) !== pbuId)
          .slice(0, 5)
          .reverse();
        const after = (afterRes?.priceBuildUps || [])
          .filter((item) => (item.priceBuildupId ?? item.priceBuildUpId) !== pbuId)
          .slice(0, 5);
        const current = { priceBuildupId: pbuId, effectiveAt, status: pbu.status };
        setCarouselItems([...before, current, ...after]);
      } catch {
        // silently ignore
      } finally {
        setCarouselLoading(false);
      }
    },
    [fetchPriceBuildUpsRequest],
  );

  useMount(async () => {
    try {
      const [res] = await Promise.all([
        loadData(),
        fetchDepots({ perPage: 1000 }).then((depotsRes) => {
          if (depotsRes?.depots) {
            const map = depotsRes.depots.reduce((acc, d) => {
              acc[d.depotId] = d.depotCode;
              return acc;
            }, {});
            setDepotMap(map);
          }
        }),
      ]);
      if (res) loadCarousel(res);
    } finally {
      setLoading(false);
    }
  });

  const isScheduled = (priceBuildUp?.status || "").toUpperCase() === "SCHEDULED";
  const isActive = (priceBuildUp?.status || "").toUpperCase() === "ACTIVE";
  const isExpired = (priceBuildUp?.status || "").toUpperCase() === "EXPIRED";

  // ── Handlers ──
  const handleEdit = useCallback(() => {
    const id = priceBuildUp?.priceBuildupId ?? priceBuildUpId;
    history.push(Path.EditPriceBuildUp, { priceBuildUpId: id });
  }, [history, priceBuildUp, priceBuildUpId]);

  const handleCancelClick = useCallback(() => {
    confirmModal.show();
  }, [confirmModal]);

  const handleCancelClickWithReason = useCallback(() => {
    setCancelReason("");
    reasonModal.show();
  }, [reasonModal]);

  const handleConfirmCancelWithReason = useCallback(async () => {
    if (!cancelReason.trim()) return;
    const id = priceBuildUp?.priceBuildupId ?? priceBuildUpId;
    setIsCancelling(true);
    try {
      await requestCancel({ priceBuildUpId: id, remarks: cancelReason });
      reasonModal.close();
      successModal.show();
    } catch {
      // error handled by useApi
    } finally {
      setIsCancelling(false);
    }
  }, [priceBuildUp, priceBuildUpId, cancelReason, requestCancel, reasonModal, successModal]);

  const handleConfirmCancel = useCallback(async () => {
    const id = priceBuildUp?.priceBuildupId ?? priceBuildUpId;
    setIsCancelling(true);
    try {
      await requestCancel({ priceBuildUpId: id });
      confirmModal.close();
      successModal.show();
    } catch {
      // error modal handled by useApi
    } finally {
      setIsCancelling(false);
    }
  }, [priceBuildUp, priceBuildUpId, requestCancel, confirmModal, successModal]);

  const handleSuccessGotIt = useCallback(() => {
    successModal.close();
    loadData().then((res) => {
      if (res) loadCarousel(res);
    });
  }, [successModal, loadData, loadCarousel]);

  const handleCarouselSelect = useCallback(
    async (selectedId) => {
      const numId = Number(selectedId);
      const currentId = priceBuildUp?.priceBuildupId;
      if (!numId || numId === currentId) return;
      setDetailLoading(true);
      try {
        const res = await loadData(numId);
        if (res) await loadCarousel(res);
      } finally {
        setDetailLoading(false);
      }
    },
    [priceBuildUp, loadData, loadCarousel],
  );

  // ── Derived data ──
  // Station comes nested in the API response
  const station = priceBuildUp?.station;

  const detailsByCode = (priceBuildUp?.priceBuildupDetails || []).reduce((acc, d) => {
    acc[d.productCode] = d;
    return acc;
  }, {});

  const prevDetailsByCode = (priceBuildUp?.prevPriceBuildup?.priceBuildupDetails || []).reduce(
    (acc, d) => {
      acc[d.productCode] = d;
      return acc;
    },
    {},
  );

  // Show only products the station has (those present in priceBuildupDetails)
  const visibleProducts = PRODUCTS.filter((p) => detailsByCode[p.code]);

  const getDetail = (code) => detailsByCode[code] || {};

  const getOldFinalWpp = (code) => {
    const d = getDetail(code);
    if (d.oldFinalWpp) return d.oldFinalWpp;
    return prevDetailsByCode[code]?.finalWpp ?? (priceBuildUp?.prevPriceBuildup ? 0 : null);
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className={`${styles.container} ${styles.fadeIn}`}>
        <Skeleton variant="text" width={140} height={18} style={{ marginBottom: 24 }} />
        <div className={styles.header}>
          <Skeleton variant="text" width={240} height={40} />
          <div className={styles.headerActions}>
            <Skeleton variant="rect" width={120} height={36} style={{ borderRadius: 6 }} />
            <Skeleton variant="rect" width={80} height={36} style={{ borderRadius: 6 }} />
          </div>
        </div>
        <div className={styles.section}>
          <Skeleton
            variant="rect"
            width={220}
            height={36}
            style={{ borderRadius: 4, marginBottom: 16 }}
          />
          {[170, 210, 130, 150, 190].map((w, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 40, marginBottom: 12, alignItems: "center" }}
            >
              <Skeleton variant="text" width={220} height={18} />
              <Skeleton variant="text" width={w} height={18} />
            </div>
          ))}
        </div>
        <div className={styles.section}>
          <Skeleton variant="text" width={180} height={22} style={{ marginBottom: 16 }} />
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 40, marginBottom: 14, alignItems: "center" }}
            >
              <Skeleton variant="text" width={220} height={18} />
              <Skeleton variant="text" width={160} height={18} />
            </div>
          ))}
        </div>
        <Skeleton variant="rect" width="100%" height={380} style={{ borderRadius: 8 }} />
      </div>
    );
  }

  // ── Render ──
  return (
    <div className={`${styles.container} ${styles.fadeIn}`}>
      <ConfirmModal
        {...confirmModal}
        title={locale.cancelPriceBuildUp}
        content={<Text style={{ color: "black" }}>{locale.cancelPriceBuildUpContent}</Text>}
        primary={{
          text: locale.yesCancel,
          onClick: handleConfirmCancel,
          loading: isCancelling,
        }}
        secondary={{ text: locale.goBack }}
      />
      <ConfirmModal
        {...reasonModal}
        title={locale.cancelPriceBuildUp}
        content={
          <div>
            <div
              style={{
                color: "black",
                fontSize: 13,
                textAlign: "center",
                width: "350px",
                margin: "auto",
              }}
            >
              Are you sure you want to cancel{" "}
              <strong>{station?.businessName || station?.customerName || ""}</strong> Price Build
              Up?
            </div>
            <Text align={"left"} strong style={{ color: "black", marginTop: "1em" }}>
              {locale.remarks}
            </Text>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value.slice(0, 255))}
              rows={4}
              maxLength={225}
              placeholder="Type your remarks here"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: 4,
                fontFamily: "inherit",
                fontSize: 13,
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
          </div>
        }
        primary={{
          text: locale.yesCancel,
          onClick: handleConfirmCancelWithReason,
          loading: isCancelling,
          disabled: !cancelReason.trim(),
        }}
        secondary={{ text: locale.goBack }}
      />
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.priceBuildUpSuccessfullyCancelled}
        primary={{ text: "Got It", onClick: handleSuccessGotIt }}
      />

      <BackLink text={locale.priceBuildUp} path={Path.PriceBuildUpList} />

      <div className={styles.header}>
        <Title>{locale.viewPriceBuildUp}</Title>
        {(canEditPbu || canCancelPbu) && (isScheduled || isActive || isExpired) && (
          <div className={styles.headerActions}>
            {isScheduled && (
              <>
                {canCancelPbu && (
                  <Button outline onClick={handleCancelClick} data-testid="pbu-view-cancel-btn">
                    {locale.cancelPriceBuildUpBtn}
                  </Button>
                )}
                {canEditPbu && (
                  <Button
                    primary
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    data-testid="pbu-view-edit-btn"
                  >
                    {locale.edit}
                  </Button>
                )}
              </>
            )}
            {(isActive || isExpired) && (
              <>
                {canCancelPbu && (
                  <Button
                    outline
                    onClick={handleCancelClickWithReason}
                    data-testid="pbu-view-cancel-btn"
                  >
                    {locale.cancelPriceBuildUpBtn}
                  </Button>
                )}
                {canEditPbu && (
                  <Button
                    primary
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    data-testid="pbu-view-edit-btn"
                  >
                    {locale.edit}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Station Details ── */}
      {priceBuildUp && (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>{locale.stationDetails}</Text>
          <div className={styles.stationDetailsGrid}>
            <div className={styles.stationDetailItem}>
              <Text className={styles.detailLabel}>{locale.stationCode}</Text>
              <Text className={styles.detailValue}>{station?.stationCode || locale.NA}</Text>
            </div>
            <div className={styles.stationDetailItem}>
              <Text className={styles.detailLabel}>{locale.stationName}</Text>
              <Text className={styles.detailValue}>{station?.name || locale.NA}</Text>
            </div>
            <div className={styles.stationDetailItem}>
              <Text className={styles.detailLabel}>{locale.customerName}</Text>
              <Text className={styles.detailValue}>
                {station?.businessName || station?.customerName || locale.NA}
              </Text>
            </div>
            <div className={styles.stationDetailItem}>
              <Text className={styles.detailLabel}>{locale.stationType}</Text>
              <Text className={styles.detailValue}>{station?.stationType || locale.NA}</Text>
            </div>
            <div className={styles.stationDetailItem}>
              <Text className={styles.detailLabel}>{locale.island}</Text>
              <Text className={styles.detailValue}>
                {station?.island?.toUpperCase() || locale.NA}
              </Text>
            </div>
            <div className={styles.stationDetailItem}>
              <Text className={styles.detailLabel}>{locale.area}</Text>
              <Text className={styles.detailValue}>{station?.area || locale.NA}</Text>
            </div>
          </div>
        </div>
      )}

      {/* ── Price Build-Up Details ── */}
      {priceBuildUp && (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>{locale.priceBuildUpDetails}</Text>

          {/* Effective date + Status */}
          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <Text className={styles.detailLabel}>{locale.effectiveDateAndTime}</Text>
              {carouselItems.length > 1 ? (
                <Autocomplete
                  className={styles.carouselSelect}
                  value={
                    priceBuildUp?.priceBuildupId != null ? String(priceBuildUp.priceBuildupId) : ""
                  }
                  options={carouselItems.map((item) => ({
                    label: formatDate(item.effectiveAt),
                    value: String(item.priceBuildupId),
                    dateStr: item.effectiveAt
                      ? moment(item.effectiveAt).format("MMMM DD, YYYY")
                      : "",
                    timeStr: item.effectiveAt ? moment(item.effectiveAt).format("hh:mm A") : "",
                  }))}
                  disableClearable
                  disabled={carouselLoading || detailLoading}
                  onChange={(_, { value }) => handleCarouselSelect(value)}
                  renderOption={(option) => (
                    <div className={styles.carouselOption}>
                      <span className={styles.carouselDate}>{option.dateStr}</span>
                      <span>{option.timeStr}</span>
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <div
                            className={styles.carouselOption}
                            style={{ flex: 1, pointerEvents: "none", paddingLeft: "5px" }}
                          >
                            <span className={styles.carouselDate}>
                              {priceBuildUp?.effectiveAt
                                ? moment(priceBuildUp.effectiveAt).format("MMMM DD, YYYY")
                                : ""}
                            </span>
                            <span>
                              {priceBuildUp?.effectiveAt
                                ? moment(priceBuildUp.effectiveAt).format("hh:mm A")
                                : ""}
                            </span>
                          </div>
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: { display: "none" },
                      }}
                    />
                  )}
                />
              ) : (
                <Text className={styles.detailValue}>{formatDate(priceBuildUp.effectiveAt)}</Text>
              )}
            </div>
            <div className={styles.metaItem}>
              <Text className={styles.detailLabel}>{locale.status}</Text>
              <StatusPill status={priceBuildUp.status} />
            </div>
            <div className={styles.metaItem}>
              <Text className={styles.detailLabel}>{locale.priceType}</Text>
              <Text className={styles.detailValue}>
                {{
                  "build-up": locale.buildUp,
                  "nationwide-movement": locale.nationwideMovement,
                  "franchise-movement": locale.franchiseMovement,
                }[priceBuildUp.type] || locale.NA}
              </Text>
            </div>
          </div>

          {/* Price table — read-only, same layout as Add page */}
          {visibleProducts.length > 0 && (
            <div className={styles.tableWrapper}>
              <table className={styles.priceTable}>
                <thead>
                  <tr>
                    <th className={styles.groupHeader}></th>
                    <th className={styles.fieldHeader}></th>
                    {visibleProducts.map((p) => (
                      <th key={p.code} className={styles.productHeader}>
                        <span
                          className={styles.productBadge}
                          style={prettifyProductCodeStyle(p.code)}
                        />
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* ── FREIGHT ── */}
                  <tr>
                    <td rowSpan={2} className={styles.groupCell}>
                      {locale.freight}
                    </td>
                    <td className={styles.fieldCell}>{locale.depot}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      const depotName = depotMap[d.depotId] || locale.NA;
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{depotName}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.freight}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt4(d.freight)}</Text>
                        </td>
                      );
                    })}
                  </tr>

                  {/* ── PUMP PRICE ── */}
                  <tr>
                    <td rowSpan={3} className={styles.groupCell}>
                      {locale.pumpPrice}
                    </td>
                    <td className={styles.fieldCell}>{locale.pumpPrice}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt2(d.pumpPrice)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.adjustment}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      const adj =
                        d.adjustment != null
                          ? parseFloat(d.adjustment)
                          : parseFloat(d.pumpPrice || 0) - parseFloat(d.oldPumpPrice || 0);
                      return (
                        <td key={p.code} style={{ backgroundColor: "#f6f6f6" }}>
                          <Text
                            className={classNames(styles.cellValue, {
                              [styles.positive]: adj > 0,
                              [styles.negative]: adj < 0,
                            })}
                          >
                            {fmt2(adj)}
                          </Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.previousPumpPrice}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td
                          key={p.code}
                          style={{
                            backgroundColor: priceBuildUp?.previousPumpPriceDisabled
                              ? "#f6f6f6"
                              : undefined,
                          }}
                        >
                          <Text className={styles.cellValue}>{fmt2(d.oldPumpPrice)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  {/* ── SUBSIDIES ── LBAM / PBC / PAM / TOTAL as individual rows */}
                  <tr>
                    <td rowSpan={4} className={styles.groupCell}>
                      {locale.subsidies}
                    </td>
                    <td className={styles.fieldCell}>LBAM</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt2(d.lbam)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>PBC</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt2(d.pbc)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>PAM</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt2(d.pam)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.subsidiesTotal}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code} style={{ backgroundColor: "#f6f6f6" }}>
                          <Text className={styles.cellValue}>{fmt2(d.totalSubsidies)}</Text>
                        </td>
                      );
                    })}
                  </tr>

                  {/* ── MARGINS ── */}
                  <tr>
                    <td className={styles.groupCell}>{locale.margins}</td>
                    <td className={styles.fieldCell}>{locale.stationUm}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt2(d.stationUm)}</Text>
                        </td>
                      );
                    })}
                  </tr>

                  {/* ── WHOLESALE POSTED PRICE ── */}
                  <tr>
                    <td rowSpan={4} className={styles.groupCell}>
                      {locale.wholesalePostedPrice}
                    </td>
                    <td className={styles.fieldCell}>{locale.netWpp}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td
                          key={p.code}
                          style={{
                            backgroundColor: priceBuildUp?.previousPumpPriceDisabled
                              ? "#f6f6f6"
                              : undefined,
                          }}
                        >
                          <Text className={styles.cellValue}>{fmt4(d.wpp)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.wppAdjustment}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code}>
                          <Text className={styles.cellValue}>{fmt4(d.wppAdjustment)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.orderingWpp}</td>
                    {visibleProducts.map((p) => {
                      const d = getDetail(p.code);
                      return (
                        <td key={p.code} style={{ backgroundColor: "#f6f6f6" }}>
                          <Text className={styles.cellValue}>{fmt4(d.finalWpp)}</Text>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className={styles.fieldCell}>{locale.previousFinalWpp}</td>
                    {visibleProducts.map((p) => (
                      <td
                        key={p.code}
                        style={{
                          backgroundColor: priceBuildUp?.previousPumpPriceDisabled
                            ? "#f6f6f6"
                            : undefined,
                        }}
                      >
                        <Text className={styles.cellValue}>
                          {fmt4(getOldFinalWpp(p.code) ?? 0)}
                        </Text>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceBuildUpViewModule;
